import { useState } from 'react'
import { Card, CardContent, CardHeader, Badge, Button } from '@repo/ui-core'
import { Project, Task, Milestone, TimeEntry } from '@repo/entity-pm'

// Mock Data
const MOCK_PROJECTS: Project[] = [
  { id: 'PROJ-1', title: 'ERP Migration', clientContact: 'Juan Dela Cruz', budget: 150000, startDate: '2023-01-01', endDate: '2023-06-30', status: 'Active', createdAt: Date.now(), updatedAt: Date.now() },
  { id: 'PROJ-2', title: 'Annual Audit 2023', clientContact: 'Maria Clara', budget: 50000, startDate: '2024-01-01', endDate: '2024-03-31', status: 'Planning', createdAt: Date.now(), updatedAt: Date.now() }
]

const MOCK_TASKS: Task[] = [
  { id: 'TASK-1', projectId: 'PROJ-1', title: 'Data Export', assignee: 'Alice', priority: 'High', dueDate: '2023-02-15', status: 'Done', createdAt: Date.now(), updatedAt: Date.now() },
  { id: 'TASK-2', projectId: 'PROJ-1', title: 'Data Cleaning', assignee: 'Bob', priority: 'Critical', dueDate: '2023-03-01', status: 'InProgress', createdAt: Date.now(), updatedAt: Date.now() },
  { id: 'TASK-3', projectId: 'PROJ-1', title: 'Data Import', assignee: 'Charlie', priority: 'Medium', dueDate: '2023-03-15', status: 'Todo', createdAt: Date.now(), updatedAt: Date.now() },
  { id: 'TASK-4', projectId: 'PROJ-2', title: 'Initial Meeting', assignee: 'Alice', priority: 'Low', dueDate: '2024-01-10', status: 'Todo', createdAt: Date.now(), updatedAt: Date.now() }
]

const MOCK_MILESTONES: Milestone[] = [
  { id: 'MILE-1', projectId: 'PROJ-1', title: 'Data Cleaned', targetDate: '2023-03-05', isCompleted: false }
]

const MOCK_TIME_ENTRIES: TimeEntry[] = [
  { id: 'TE-1', taskId: 'TASK-1', projectId: 'PROJ-1', accountantId: 'ACC-1', hoursSpent: 5, hourlyRate: 1500, billableAmount: 7500, isBillable: true, isBilled: false, date: '2023-02-14' }
]

export const ProjectsDashboardPage = () => {
  const [activeProjectId, setActiveProjectId] = useState<string>(MOCK_PROJECTS[0]?.id ?? '')

  
  const formatPHP = (amount: number) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount)
  }

  const activeProject = MOCK_PROJECTS.find(p => p.id === activeProjectId)
  const projectTasks = MOCK_TASKS.filter(t => t.projectId === activeProjectId)
  const projectMilestones = MOCK_MILESTONES.filter(m => m.projectId === activeProjectId)
  const projectTimeEntries = MOCK_TIME_ENTRIES.filter(te => te.projectId === activeProjectId)

  const TASK_STATUSES = ['Todo', 'InProgress', 'Review', 'Done'] as const
  const totalBillable = projectTimeEntries.reduce((sum, te) => sum + te.billableAmount, 0)
  const totalHours = projectTimeEntries.reduce((sum, te) => sum + te.hoursSpent, 0)

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects & Tasks</h1>
          <p className="text-sm text-gray-500">Manage client projects, deliverables, and billing</p>
        </div>
        <div className="flex space-x-2">
          <Button>New Project</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Project List */}
        <div className="col-span-1 space-y-4">
          <h2 className="font-semibold text-lg">Active Projects</h2>
          {MOCK_PROJECTS.map(proj => (
            <div 
              key={proj.id} 
              className={`cursor-pointer transition-colors rounded-xl border ${activeProjectId === proj.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              onClick={() => setActiveProjectId(proj.id)}
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900">{proj.title}</h3>
                  <Badge color={proj.status === 'Active' ? 'green' : 'gray'}>{proj.status}</Badge>
                </div>
                <div className="text-sm text-gray-600 mb-1">Client: {proj.clientContact}</div>
                <div className="text-sm font-medium text-green-700">Budget: {formatPHP(proj.budget)}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {proj.startDate} to {proj.endDate}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Project Detail View */}
        <div className="col-span-3 space-y-6">
          <div className="flex justify-between items-center border-b pb-4">
            <div>
              <h2 className="font-semibold text-2xl">{activeProject?.title}</h2>
              <div className="text-sm text-gray-500 mt-1">
                Client: {activeProject?.clientContact} | Budget: {formatPHP(activeProject?.budget || 0)}
              </div>
            </div>
            <Button variant="outline" size="sm">Generate Invoice</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card padding="md">
              <CardHeader title="Billing Summary" />
              <CardContent>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Total Hours</span>
                  <span className="font-semibold">{totalHours} hrs</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Billable Amount</span>
                  <span className="font-semibold text-green-600">{formatPHP(totalBillable)}</span>
                </div>
              </CardContent>
            </Card>

            <Card padding="md">
              <CardHeader title="Milestones" />
              <CardContent>
                <div className="space-y-2">
                  {projectMilestones.map(m => (
                    <div key={m.id} className="flex justify-between items-center text-sm">
                      <span className={m.isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}>{m.title}</span>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{m.targetDate}</span>
                    </div>
                  ))}
                  {projectMilestones.length === 0 && <div className="text-gray-400 text-sm">No milestones</div>}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-4 mt-6">
              <h2 className="font-semibold text-lg">Task Kanban</h2>
              <Button variant="outline" size="sm">Add Task</Button>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-4">
              {TASK_STATUSES.map(status => {
                const statusTasks = projectTasks.filter(t => t.status === status)
                return (
                  <div key={status} className="flex-none w-64 bg-gray-100 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-700">{status}</h3>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                        {statusTasks.length}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {statusTasks.map(task => (
                        <Card key={task.id} padding="sm" className="bg-white shadow-sm border border-gray-200">
                          <CardContent>
                            <div className="font-medium text-sm text-gray-900 mb-1">{task.title}</div>
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-xs text-gray-500">
                                {task.assignee}
                              </span>
                              <Badge 
                                color={task.priority === 'Critical' ? 'red' : task.priority === 'High' ? 'yellow' : 'gray'}
                              >
                                {task.priority}
                              </Badge>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-2">Due: {task.dueDate}</div>
                          </CardContent>
                        </Card>
                      ))}
                      {statusTasks.length === 0 && (
                        <div className="text-center py-4 text-xs text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                          No tasks
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
